import { z } from "zod";
import {
  documentListSchema,
  updateDocumentRequestSchema,
  uploadDocumentRequestSchema,
  uploadTargetSchema,
} from "./documents.ts";
import {
  createFolderRequestSchema,
  folderSchema,
  folderSectionSchema,
  movedItemSchema,
  moveFolderRequestSchema,
  moveItemRequestSchema,
  renameFolderRequestSchema,
} from "./folders.ts";
import {
  citationStatusSchema,
  classificationLevelSchema,
  documentIdSchema,
  passageProvenanceSchema,
  storedTrustSchema,
} from "./knowledge.ts";
import { residencySchema, residencyViewSchema } from "./models.ts";
import { projectSummarySchema } from "./project-summary.ts";
import { conversationIdSchema, projectIdSchema, projectRoleSchema } from "./projects.ts";

/** IDs in tool arguments: UUIDs only (looked up inside the token's tenant, never trusted). */
const toolUuid = z.uuid();

/**
 * ESI as an MCP server (spec `mcp-server`, openspec change `mcp-server-core`): the contracts of
 * the read tools, shared by the server and its tests. Tool names live in the MCP server's own
 * namespace, distinct from the agent's tools. Additive from one release to the next: a field or
 * a tool is removed only one release after a deprecation note in the tool's description.
 */

/** The MCP server's path under the public URL; tokens are bound to `<publicUrl>/mcp`. */
export const MCP_PATH = "/mcp";

export const MCP_TOOL_NAMES = [
  "list_projects",
  "search_knowledge",
  "read_document",
  "read_wiki_page",
  "ask_project",
  "save_note",
  "save_to_wiki",
  // mcp-foundations-files: files and folders (the `knowledge` group).
  "list_files",
  "get_file",
  "upload_file",
  "confirm_upload",
  "download_file",
  "update_file",
  "list_folders",
  "create_folder",
  "rename_folder",
  "move_folder",
  "move_item",
  "delete_folder",
  // project-home: the project summary (the `projects` group).
  "get_project_summary",
] as const;
export type McpToolName = (typeof MCP_TOOL_NAMES)[number];

/**
 * The write tools (mcp-write-tools): listed only for tokens holding the MCP resource's `write`
 * scope in a tenant where `mcp.writeTools` is on at the root; refused otherwise.
 */
export const MCP_WRITE_TOOL_NAMES = [
  "save_note",
  "save_to_wiki",
  "upload_file",
  "confirm_upload",
  "update_file",
  "create_folder",
  "rename_folder",
  "move_folder",
  "move_item",
  "delete_folder",
] as const satisfies readonly McpToolName[];
export type McpWriteToolName = (typeof MCP_WRITE_TOOL_NAMES)[number];
export const isMcpWriteTool = (name: string): name is McpWriteToolName =>
  (MCP_WRITE_TOOL_NAMES as readonly string[]).includes(name);

/**
 * The tool groups (ADR 0018 §1): each is switched by `mcp.tools.<group>`, lockable at tenant and
 * entity and narrow-only per project, and gates every tool in it, reads included.
 * `list_projects` is in no group.
 */
export const MCP_TOOL_GROUPS = ["knowledge", "wiki", "tasks", "projects", "conversations"] as const;
export const mcpToolGroupSchema = z.enum(MCP_TOOL_GROUPS);
export type McpToolGroup = z.infer<typeof mcpToolGroupSchema>;

/**
 * The tools of each group, in `MCP_TOOL_NAMES` order: the server's declarations must agree (a
 * core test checks it). Settings screens list them, and hide a group that has none yet.
 */
export const MCP_TOOL_GROUP_TOOLS: Readonly<Record<McpToolGroup, readonly McpToolName[]>> = {
  knowledge: [
    "search_knowledge",
    "read_document",
    "save_note",
    "list_files",
    "get_file",
    "upload_file",
    "confirm_upload",
    "download_file",
    "update_file",
    "list_folders",
    "create_folder",
    "rename_folder",
    "move_folder",
    "move_item",
    "delete_folder",
  ],
  wiki: ["read_wiki_page", "save_to_wiki"],
  tasks: [],
  projects: ["get_project_summary"],
  conversations: ["ask_project"],
};

/** The groups that hold at least one tool, in `MCP_TOOL_GROUPS` order. */
export const MCP_TOOL_GROUPS_IN_USE = MCP_TOOL_GROUPS.filter(
  (group) => MCP_TOOL_GROUP_TOOLS[group].length > 0,
);

/** Limits of the write tools (mcp-write-tools D5): the Markdown of one write, its title, summary. */
export const MCP_WRITE_LIMITS = {
  noteBytes: 60 * 1024,
  titleChars: 200,
  summaryChars: 500,
} as const;

/**
 * Writes per hour of each tool group (mcp-foundations-files D7): per user, and per user and
 * client. The per-client counter is hit first, so one client past its share never spends the
 * user's other clients' share.
 */
export const MCP_GROUP_WRITE_LIMITS: Readonly<
  Record<McpToolGroup, { perUserPerHour: number; perClientPerHour: number }>
> = {
  knowledge: { perUserPerHour: 60, perClientPerHour: 30 },
  wiki: { perUserPerHour: 60, perClientPerHour: 30 },
  tasks: { perUserPerHour: 120, perClientPerHour: 60 },
  projects: { perUserPerHour: 30, perClientPerHour: 15 },
  conversations: { perUserPerHour: 60, perClientPerHour: 30 },
};

/** A JSON-RPC body of `/mcp` at most (D8): larger bodies get `413` before they are parsed. */
export const MCP_MAX_BODY_BYTES = 512 * 1024;

/** Items (files, pages, tasks) one tool call may act on (D8); more is `payload_too_large`. */
export const MCP_MAX_ITEMS_PER_CALL = 20;

/**
 * A creating tool's idempotency key (D6): scoped to tenant, user, client and tool, kept 24
 * hours. A repeat with the same arguments returns the first result; other arguments, `conflict`.
 */
export const mcpIdempotencyKeySchema = z
  .string()
  .min(1)
  .max(64)
  .describe(
    "Optional: a key of your own (1 to 64 characters) that makes a retry return the first call's result instead of creating again, for 24 hours",
  );

/**
 * `mcp.classificationCeiling`: the widest readers floor of content that may be returned to an
 * MCP client (`project` < `entity` < `named`, from least to most restricted).
 */
export const mcpClassificationCeilingSchema = classificationLevelSchema.shape.readersFloor;
export type McpClassificationCeiling = z.infer<typeof mcpClassificationCeilingSchema>;

/** Projects listed by `list_projects` at most. */
export const MCP_MAX_PROJECTS = 200;
/** Search passages: default and maximum per call. */
export const MCP_SEARCH_DEFAULT_LIMIT = 8;
export const MCP_SEARCH_MAX_LIMIT = 20;
export const MCP_QUERY_MAX_CHARS = 500;
/** Passages of one section per `read_document` call, continued with `offset`. */
export const MCP_SECTION_PASSAGES = 20;
/** Characters of a wiki page per `read_wiki_page` call, cut on a line boundary. */
export const MCP_WIKI_PAGE_CHARS = 16_000;
/** Characters of one passage returned (as the agent's own tool). */
export const MCP_PASSAGE_MAX_CHARS = 4000;
/**
 * Characters of a citation's passage `excerpt` in an `ask_project` result at most, cut on a word
 * and then marked with an ellipsis (openspec change `mcp-citations-residency`).
 */
export const MCP_CITATION_EXCERPT_CHARS = 600;
/** Statements listed per `ask_project` statement list (`uncited`, `unverified`) at most. */
export const MCP_ASK_STATEMENTS_MAX = 20;
/** Characters of one listed statement, cut on a word and then marked with an ellipsis. */
export const MCP_ASK_STATEMENT_CHARS = 500;
/** Characters of an `ask_project` question. */
export const MCP_QUESTION_MAX_CHARS = 4000;

/** Why a tool call was refused or failed: a tool result marked as an error, never HTTP. */
export const MCP_ERROR_CODES = [
  "mcp_disabled",
  "not_found",
  "invalid_arguments",
  "throttled",
  "model_budget",
  // The caller's share of the monthly AI budget is used up (`models.userMonthlySharePct`).
  "user_budget",
  "unavailable",
  // ask_project (mcp-ask-project): the answer relies on sources above the ceiling; the
  // project is frozen; the conversation is answering another question.
  "withheld",
  "frozen",
  "busy",
  // The write tools (mcp-write-tools): refused for a reason the client can show
  // (`insufficient_scope`, `write_disabled`, `permission`, `storage_limit`,
  // `tool_group_disabled`).
  "forbidden",
  // ADR 0018 §7: an operation that belongs to ESI or the `esi` CLI (with `cli` and `url`); an
  // idempotency key reused with other arguments; a stale `expectedVersion` (with
  // `currentVersion`); a call naming too many items.
  "not_in_mcp",
  "conflict",
  "precondition_failed",
  "payload_too_large",
] as const;
export const mcpErrorCodeSchema = z.enum(MCP_ERROR_CODES);
export type McpErrorCode = z.infer<typeof mcpErrorCodeSchema>;

export const mcpToolErrorSchema = z.object({
  code: mcpErrorCodeSchema,
  message: z.string(),
  /** Seconds before a `throttled` call may be retried. */
  retryAfter: z.int().min(0).optional(),
  /** Why a `forbidden` call was refused. */
  reason: z.string().optional(),
  /** `not_in_mcp`: the `esi` command that does it. */
  cli: z.string().optional(),
  /** `not_in_mcp`: the ESI page where a person does it (sign-in required). */
  url: z.string().optional(),
  /** `precondition_failed`: the version the target is at now. */
  currentVersion: z.int().min(1).optional(),
});
export type McpToolError = z.infer<typeof mcpToolErrorSchema>;

/** Where a tool error's code travels: the result's `_meta` (and its text), never `structuredContent`. */
export const MCP_ERROR_META_KEY = "esi.dev/error";

// ── Inputs ──────────────────────────────────────────────────────────────────────────────────

const projectId = toolUuid.describe("A project ID from list_projects");

/** Exactly one way of addressing a part of a document. */
export const mcpSectionSelectorSchema = z.union([
  z.strictObject({
    sectionPath: z
      .array(z.string().min(1).max(300))
      .max(12)
      .describe("Section titles from the outline, top level first ([] for the document start)"),
  }),
  z.strictObject({ page: z.int().min(1).max(100_000) }),
  z.strictObject({ slide: z.int().min(1).max(10_000) }),
  z.strictObject({ sheet: z.string().min(1).max(200) }),
]);
export type McpSectionSelector = z.infer<typeof mcpSectionSelectorSchema>;

export const listProjectsInputSchema = z.strictObject({});

export const searchKnowledgeInputSchema = z.strictObject({
  projectId,
  query: z.string().trim().min(1).max(MCP_QUERY_MAX_CHARS).describe("What to look for"),
  limit: z
    .int()
    .min(1)
    .max(MCP_SEARCH_MAX_LIMIT)
    .optional()
    .describe(`Passages to return (default ${MCP_SEARCH_DEFAULT_LIMIT})`),
});
export type SearchKnowledgeInput = z.infer<typeof searchKnowledgeInputSchema>;

export const readDocumentInputSchema = z.strictObject({
  projectId,
  documentId: toolUuid.describe("A document ID from search_knowledge"),
  section: mcpSectionSelectorSchema
    .optional()
    .describe("One part to read: a section path, a page, a slide or a sheet; omit for the outline"),
  offset: z
    .int()
    .min(0)
    .max(100_000)
    .optional()
    .describe("Passages to skip in the section (from a previous call's offset)"),
});
export type ReadDocumentInput = z.infer<typeof readDocumentInputSchema>;

export const readWikiPageInputSchema = z.strictObject({
  projectId,
  pageId: toolUuid.describe("A wiki page's document ID"),
  offset: z
    .int()
    .min(0)
    .max(10_000_000)
    .optional()
    .describe("Character offset to continue from (a previous call's nextOffset)"),
});
export type ReadWikiPageInput = z.infer<typeof readWikiPageInputSchema>;

export const askProjectInputSchema = z.strictObject({
  projectId,
  question: z
    .string()
    .trim()
    .min(1)
    .max(MCP_QUESTION_MAX_CHARS)
    .describe("The question for the project's agent, in the user's words"),
  conversationId: toolUuid
    .optional()
    .describe("Continue a conversation an earlier ask_project call returned"),
});
export type AskProjectInput = z.infer<typeof askProjectInputSchema>;

const writeTitle = z.string().trim().min(1).max(MCP_WRITE_LIMITS.titleChars);

/**
 * Markdown of a write: checked for size before anything else reads it (characters first, which
 * never undercount UTF-8 bytes, then bytes). Never interpreted: no front matter, no links followed.
 */
const writeMarkdown = z
  .string()
  .min(1)
  .max(MCP_WRITE_LIMITS.noteBytes)
  .refine(
    (text) => new TextEncoder().encode(text).byteLength <= MCP_WRITE_LIMITS.noteBytes,
    `At most ${MCP_WRITE_LIMITS.noteBytes / 1024} KiB of UTF-8 Markdown`,
  );

export const saveNoteInputSchema = z.strictObject({
  projectId,
  title: writeTitle.describe("The note's file title"),
  markdown: writeMarkdown.describe(
    "The note, in Markdown: at most 60 KiB of UTF-8. Saved as written, marked as external content",
  ),
  classification: z
    .string()
    .min(1)
    .max(64)
    .optional()
    .describe("A classification level ID of the organisation; the project's default when omitted"),
  idempotencyKey: mcpIdempotencyKeySchema.optional(),
});
export type SaveNoteInput = z.infer<typeof saveNoteInputSchema>;

export const saveToWikiInputSchema = z.strictObject({
  projectId,
  title: writeTitle.describe("The page's title"),
  markdown: writeMarkdown.describe("The page's proposed Markdown: at most 60 KiB of UTF-8"),
  pageId: toolUuid
    .optional()
    .describe("An existing wiki page to propose a change to; omit to propose a new page"),
  summary: z
    .string()
    .trim()
    .min(1)
    .max(MCP_WRITE_LIMITS.summaryChars)
    .describe("One or two sentences for the reviewer: what changes and why"),
});
export type SaveToWikiInput = z.infer<typeof saveToWikiInputSchema>;

// Files and folders (mcp-foundations-files D2): each input is the route's request schema, less
// what MCP may not set, plus the path's IDs; `mcp-files.test.ts` keeps them in step.

const documentId = toolUuid.describe("A file's document ID, from list_files");
const folderId = toolUuid.describe("A folder ID, from list_folders");

export const listFilesInputSchema = z.strictObject({
  projectId,
  cursor: z
    .string()
    .regex(/^\d{1,6}$/)
    .optional()
    .describe("Where to continue: a previous call's nextCursor"),
});
export type ListFilesInput = z.infer<typeof listFilesInputSchema>;

export const getFileInputSchema = z.strictObject({ projectId, documentId });
export type GetFileInput = z.infer<typeof getFileInputSchema>;

/** `get_project_summary` (project-home): the project's summary counts. */
export const getProjectSummaryInputSchema = z.strictObject({ projectId });
export type GetProjectSummaryInput = z.infer<typeof getProjectSummaryInputSchema>;

/** `upload_file`: the upload intent less `origin`, which is always `external` (ADR 0009). */
export const uploadFileInputSchema = uploadDocumentRequestSchema
  .omit({ origin: true })
  .extend({ projectId, idempotencyKey: mcpIdempotencyKeySchema.optional() })
  .strict();
export type UploadFileInput = z.infer<typeof uploadFileInputSchema>;

export const confirmUploadInputSchema = z.strictObject({ projectId, documentId });
export type ConfirmUploadInput = z.infer<typeof confirmUploadInputSchema>;

export const downloadFileInputSchema = z.strictObject({ projectId, documentId });
export type DownloadFileInput = z.infer<typeof downloadFileInputSchema>;

/** `update_file`: the metadata change less `origin`; readers may only narrow, levels only rise. */
export const updateFileInputSchema = updateDocumentRequestSchema
  .omit({ origin: true, expectedVersion: true })
  .extend({
    projectId,
    documentId,
    expectedVersion: z
      .int()
      .min(1)
      .optional()
      .describe(
        "The file's metadataVersion as you read it: a different current one refuses the change",
      ),
  })
  .strict();
export type UpdateFileInput = z.infer<typeof updateFileInputSchema>;

export const listFoldersInputSchema = z.strictObject({
  projectId,
  section: folderSectionSchema.optional().describe("files or wiki; both when omitted"),
});
export type ListFoldersInput = z.infer<typeof listFoldersInputSchema>;

export const createFolderInputSchema = createFolderRequestSchema
  .extend({ projectId, idempotencyKey: mcpIdempotencyKeySchema.optional() })
  .strict();
export type CreateFolderInput = z.infer<typeof createFolderInputSchema>;

export const renameFolderInputSchema = renameFolderRequestSchema
  .extend({ projectId, folderId })
  .strict();
export type RenameFolderInput = z.infer<typeof renameFolderInputSchema>;

export const moveFolderInputSchema = moveFolderRequestSchema
  .extend({ projectId, folderId })
  .strict();
export type MoveFolderInput = z.infer<typeof moveFolderInputSchema>;

/** A file or a wiki page, into a folder of its own section (null: the section root). */
export const moveItemInputSchema = moveItemRequestSchema
  .extend({ projectId, documentId: toolUuid.describe("A file's or a wiki page's document ID") })
  .strict();
export type MoveItemInput = z.infer<typeof moveItemInputSchema>;

export const deleteFolderInputSchema = z.strictObject({ projectId, folderId });
export type DeleteFolderInput = z.infer<typeof deleteFolderInputSchema>;

/**
 * The keys each file and folder tool adds to its route's request schema: the path's IDs, and
 * the idempotency key and cursor MCP owns. Every other key is the route's (`mcp-files.test.ts`).
 */
export const MCP_ADDED_INPUT_KEYS = [
  "projectId",
  "documentId",
  "folderId",
  "idempotencyKey",
  "expectedVersion",
  "cursor",
  "section",
] as const;

// ── Outputs ─────────────────────────────────────────────────────────────────────────────────

/** A document as an MCP client sees it: a stable ID for later calls and a link back to ESI. */
export const mcpDocumentRefSchema = z.object({
  id: documentIdSchema,
  title: z.string(),
  kind: z.enum(["file", "wiki_page"]),
  classification: z.object({ id: z.string(), label: z.string() }),
  trust: storedTrustSchema,
  /** An ESI page that requires sign-in; never a presigned or stored URL. */
  url: z.string(),
});
export type McpDocumentRef = z.infer<typeof mcpDocumentRefSchema>;

export const mcpPassageSchema = z.object({
  document: mcpDocumentRefSchema,
  /** Readable location, e.g. `page 3, Maintenance`. */
  location: z.string(),
  text: z.string(),
  trust: storedTrustSchema,
  provenance: passageProvenanceSchema.optional(),
});
export type McpPassage = z.infer<typeof mcpPassageSchema>;

export const listProjectsOutputSchema = z.object({
  projects: z.array(
    z.object({
      id: projectIdSchema,
      name: z.string(),
      description: z.string().nullable(),
      role: projectRoleSchema,
      frozen: z.boolean(),
      url: z.string(),
      /**
       * The project's effective `models.residency` (`global`: its model calls may be processed
       * outside the EU) and whether it is locked for the project; never who or what locked it.
       */
      residency: residencyViewSchema.pick({ value: true, locked: true }),
    }),
  ),
});
export type ListProjectsOutput = z.infer<typeof listProjectsOutputSchema>;

export const searchKnowledgeOutputSchema = z.object({
  passages: z.array(mcpPassageSchema),
  /** Passages dropped by the ceiling's re-check (a reclassification since the search began). */
  withheld: z.int().min(0),
  note: z.string(),
});
export type SearchKnowledgeOutput = z.infer<typeof searchKnowledgeOutputSchema>;

export const readDocumentOutputSchema = z.object({
  document: mcpDocumentRefSchema,
  note: z.string(),
  /** Without `section`: the outline. */
  outline: z
    .array(
      z.object({
        section: z.array(z.string()).optional(),
        pages: z.object({ from: z.int(), to: z.int() }).optional(),
        slide: z.int().optional(),
        sheet: z.string().optional(),
        passages: z.int().min(0),
      }),
    )
    .optional(),
  truncated: z.boolean().optional(),
  /** With `section`: its passages from `offset`. */
  passages: z.array(mcpPassageSchema.omit({ document: true })).optional(),
  offset: z.int().min(0).optional(),
  more: z.boolean().optional(),
});
export type ReadDocumentOutput = z.infer<typeof readDocumentOutputSchema>;

export const readWikiPageOutputSchema = z.object({
  document: mcpDocumentRefSchema,
  note: z.string(),
  markdown: z.string(),
  offset: z.int().min(0),
  nextOffset: z.int().min(0).nullable(),
  version: z.int().min(1),
  /** The current version, when a newer one than the shown one exists. */
  newerVersion: z.int().min(1).optional(),
});
export type ReadWikiPageOutput = z.infer<typeof readWikiPageOutputSchema>;

/** A knowledge citation of an `ask_project` answer: its `[S#]` marker and the passage's document. */
export const mcpCitationSchema = z.object({
  handle: z.string().regex(/^S\d{1,9}$/),
  document: mcpDocumentRefSchema,
  /** Readable location, e.g. `page 3, Maintenance`. */
  location: z.string(),
  /** The post-generation check: `supported`, `unsupported` (unverified) or `unchecked`. */
  status: citationStatusSchema,
  /**
   * The cited passage's stored text, clipped to `MCP_CITATION_EXCERPT_CHARS` (an ellipsis marks
   * a cut). Absent, never partial, when the passage cannot be shown: above the ceiling, no
   * longer readable, replaced by a newer version, or without text (an image).
   */
  excerpt: z
    .string()
    .max(MCP_CITATION_EXCERPT_CHARS + 1)
    .optional(),
});
export type McpCitation = z.infer<typeof mcpCitationSchema>;

/** A web citation (`[W#]`) of an answer in a project that allows web search: `http(s)` only. */
export const mcpWebCitationSchema = z.object({
  handle: z.string().regex(/^W\d{1,9}$/),
  url: z.string(),
  title: z.string().nullable(),
});
export type McpWebCitation = z.infer<typeof mcpWebCitationSchema>;

/** Why an answer stopped early; the conversation in ESI shows the same notice. */
export const MCP_ASK_STOPS = [
  "timeout",
  "model_budget",
  "turn_budget",
  "user_budget",
  "max_steps",
  "unavailable",
] as const;
export const mcpAskStopSchema = z.enum(MCP_ASK_STOPS);
export type McpAskStop = z.infer<typeof mcpAskStopSchema>;

export const askProjectOutputSchema = z.object({
  note: z.string(),
  /** Markdown, with `[S#]` and `[W#]` markers; `[unsourced]` ends a statement without a source. */
  answer: z.string(),
  citations: z.array(mcpCitationSchema),
  webCitations: z.array(mcpWebCitationSchema),
  /** Whether a statement was marked as not drawn from the project's sources. */
  unsourced: z.boolean(),
  /**
   * The statements that cite no source (#324): how many, and the first
   * `MCP_ASK_STATEMENTS_MAX` as written in `answer` (markers kept, clipped to
   * `MCP_ASK_STATEMENT_CHARS`). Absent from servers one release behind.
   */
  uncited: z
    .object({
      count: z.int().min(0),
      statements: z.array(z.string().max(MCP_ASK_STATEMENT_CHARS + 1)).max(MCP_ASK_STATEMENTS_MAX),
    })
    .optional(),
  /**
   * The statements whose citation failed the post-generation check, with the handles that
   * failed there (the same handle may be `supported` in other statements), first
   * `MCP_ASK_STATEMENTS_MAX`. Absent from servers one release behind.
   */
  unverified: z
    .array(
      z.object({
        statement: z.string().max(MCP_ASK_STATEMENT_CHARS + 1),
        handles: z.array(z.string().regex(/^[SW]\d{1,9}$/)).min(1),
      }),
    )
    .max(MCP_ASK_STATEMENTS_MAX)
    .optional(),
  /** The model that wrote it (null when none answered) and the residency it ran under. */
  model: z.object({ name: z.string().nullable(), residency: residencySchema }),
  /** The conversation in ESI (sign-in required): continue it here or there. */
  conversation: z.object({ id: conversationIdSchema, url: z.string() }),
  stopped: mcpAskStopSchema.optional(),
});
export type AskProjectOutput = z.infer<typeof askProjectOutputSchema>;

export const saveNoteOutputSchema = z.object({
  note: z.string(),
  /** The new file: trust `external` (its content came from outside ESI). */
  document: mcpDocumentRefSchema,
  /** Searchable once ingested; `failed` when indexing could not start (retry in ESI). */
  status: z.enum(["ingesting", "failed"]),
});
export type SaveNoteOutput = z.infer<typeof saveNoteOutputSchema>;

export const saveToWikiOutputSchema = z.object({
  /**
   * The one-item change set, to review and publish in ESI (sign-in required); never published
   * here. Its `url` is its review with the proposed page selected.
   */
  changeSet: z.object({ id: z.string(), url: z.string() }),
  note: z.string(),
  /** The page the draft changes, when it changes one. */
  page: mcpDocumentRefSchema.optional(),
  /** This draft replaced the same client's earlier pending draft for the page. */
  replacedDraftId: z.string().optional(),
});
export type SaveToWikiOutput = z.infer<typeof saveToWikiOutputSchema>;

/** Files listed by one `list_files` call; `nextCursor` continues. */
export const MCP_FILES_PAGE = 100;

/**
 * A file as an MCP client sees it: the document reference (classification label, trust, ESI
 * link) and the app's listed fields, with its status and failure reason so a harness can poll
 * ingestion. Never a presigned URL.
 */
export const mcpFileSchema = mcpDocumentRefSchema.extend({
  ...documentListSchema.shape.documents.element.pick({
    folderId: true,
    mediaType: true,
    sizeBytes: true,
    version: true,
    metadataVersion: true,
    status: true,
    failureReason: true,
    origin: true,
    readers: true,
    updatedAt: true,
  }).shape,
});
export type McpFile = z.infer<typeof mcpFileSchema>;

export const listFilesOutputSchema = z.object({
  note: z.string(),
  files: z.array(mcpFileSchema),
  nextCursor: z.string().nullable(),
});
export type ListFilesOutput = z.infer<typeof listFilesOutputSchema>;

export const getFileOutputSchema = z.object({ note: z.string(), file: mcpFileSchema });
export type GetFileOutput = z.infer<typeof getFileOutputSchema>;

/**
 * The project summary as the app's Overview counts it for the caller, within the classification
 * ceiling: no restricted count and no review count (MCP never counts what the role or ceiling
 * hides), and only top-cited documents within the ceiling.
 */
export const getProjectSummaryOutputSchema = z.object({
  note: z.string(),
  summary: projectSummarySchema,
  /** The project's Overview in ESI. */
  url: z.string(),
});
export type GetProjectSummaryOutput = z.infer<typeof getProjectSummaryOutputSchema>;

export const uploadFileOutputSchema = z.object({
  note: z.string(),
  file: mcpFileSchema,
  /**
   * The single PUT (5 minutes, create-only, these headers): for this call only. Null on a
   * repeated idempotency key: call upload_file again with a new key for a new URL.
   */
  upload: uploadTargetSchema.nullable(),
});
export type UploadFileOutput = z.infer<typeof uploadFileOutputSchema>;

/** `file` null: confirmed, but its level is above what MCP clients may read. */
export const confirmUploadOutputSchema = z.object({
  note: z.string(),
  file: mcpFileSchema.nullable(),
});
export type ConfirmUploadOutput = z.infer<typeof confirmUploadOutputSchema>;

export const downloadFileOutputSchema = z.object({
  note: z.string(),
  file: mcpFileSchema,
  /** A GET URL valid 60 seconds, for this call only. */
  download: z.object({ url: z.string(), expiresInSeconds: z.int().min(1) }),
});
export type DownloadFileOutput = z.infer<typeof downloadFileOutputSchema>;

/** `hidden`: the change left the caller out of the readers; it succeeded. */
export const updateFileOutputSchema = z.object({
  note: z.string(),
  file: mcpFileSchema.nullable(),
  hidden: z.boolean(),
});
export type UpdateFileOutput = z.infer<typeof updateFileOutputSchema>;

/** A folder, with its ESI page. */
export const mcpFolderSchema = folderSchema.extend({ url: z.string() });
export type McpFolder = z.infer<typeof mcpFolderSchema>;

export const listFoldersOutputSchema = z.object({ folders: z.array(mcpFolderSchema) });
export type ListFoldersOutput = z.infer<typeof listFoldersOutputSchema>;

export const folderOutputSchema = z.object({ folder: mcpFolderSchema });
export type FolderOutput = z.infer<typeof folderOutputSchema>;

export const moveItemOutputSchema = z.object({ moved: movedItemSchema });
export type MoveItemOutput = z.infer<typeof moveItemOutputSchema>;

/** Only a strictly empty folder is deleted (nothing moves), so the answer is its ID. */
export const deleteFolderOutputSchema = z.object({ deletedFolderId: z.string() });
export type DeleteFolderOutput = z.infer<typeof deleteFolderOutputSchema>;

/** `GET /api/account/mcp`: how to connect a harness (web session only). */
export const accountMcpSchema = z.object({
  url: z.string(),
  /** `mcp.enabled` at the tenant root, for the caller. */
  enabled: z.boolean(),
});
export type AccountMcp = z.infer<typeof accountMcpSchema>;
